import { isNil, isEmpty } from "lodash";
import { checkFullUser, checkManager, checkSharedWith } from "./auth";

export const ANON_NAME = "Anonymous";

export const formatSessionLength = (sessionLen, fallbackValue = "-") => {
  if (!sessionLen || sessionLen <= 0) return fallbackValue;
  let seconds = Math.floor((sessionLen / 1000) % 60);
  let minutes = Math.floor((sessionLen / (1000 * 60)) % 60);
  let hours = Math.floor((sessionLen / (1000 * 60 * 60)) % 24);

  return seconds + minutes + hours > 0
    ? `${hours >= 1 ? hours + "h" : ""} ${minutes >= 1 ? minutes + "m" : ""} ${seconds >= 1 ? seconds + "s" : ""}`
    : "-";
};

export const numCountToRows = (countObject, prefix = "", suffix = "", max = 5) => {
  let rows = [...Array(max).keys()]
    .sort((a, b) => b - a)
    .map(i => [prefix + (i + 1) + suffix, countObject?.[`no${i + 1}`] || 0]);
  return rows;
};

export const decileCountToRows = (countObject, prefix = "", suffix = "") => {
  let rows = Object.keys(countObject || {})
    .sort((a, b) => parseInt(b, 10) - parseInt(a, 10))
    .map(key => [prefix + key + suffix, countObject?.[key] || 0]);
  return rows;
};

export const decileCountTo5Rows = (countObject, prefix = "", suffix = "") => {
  const aggregated = {};

  Object.entries(countObject || {}).forEach(([key, value]) => {
    // Determine the range the current decile belongs to
    let base = parseInt(key, 10);
    if (base === 100) base = 90; // Merge in 100% results with 80-100%
    const rangeKey = `${Math.floor(base / 20) * 20}-${Math.floor(base / 20) * 20 + 20}%`;

    // Initialize or update the count for the range
    if (!aggregated[rangeKey]) {
      aggregated[rangeKey] = value;
    } else {
      aggregated[rangeKey] += value;
    }
  });

  let rows = Object.keys(aggregated)
    .sort()
    .map(key => [prefix + key + suffix, aggregated?.[key] || 0]);
  return rows;
};

export const formatPercentage = (number, fallbackValue = "-") =>
  isNil(number) ? fallbackValue : `${Math.round(100 * (number || 0))}%`;

export const formatRating = (number, fallbackValue = "-") =>
  isNil(number) ? fallbackValue : Math.round(((number || 0) + Number.EPSILON) * 100) / 100;

export const formatSessionDate = (sessionDate, fallbackValue = "-") => {
  if (sessionDate && sessionDate.length !== "") {
    let newDate = new Date(sessionDate).toString();
    let dateArr = newDate.slice(0, 21).split(" ");
    return `${dateArr[2]} ${dateArr[1]} ${dateArr[3]} ${dateArr[4]}`;
  } else {
    return fallbackValue;
  }
};

export const formatSessionDatePair = (sessionDate, fallbackValue = "-") => {
  if (sessionDate && sessionDate.length !== "") {
    let newDate = new Date(sessionDate).toString();
    let dateArr = newDate.slice(0, 21).split(" ");
    return { date: `${dateArr[2]}\u00A0${dateArr[1]}\u00A0${dateArr[3]}`, time: `${dateArr[4]}` };
  } else {
    return fallbackValue;
  }
};

export const defaultSessionTab = "transcript";

const sessionTabFilters = ["transcript", "allEvents", "settings", "stats", "debug"];

export const getAllowedSessionTabFilters = isSuperAdmin =>
  isSuperAdmin ? sessionTabFilters : sessionTabFilters.filter(tab => tab !== "stats" && tab !== "debug");

export const parseSessionTabHash = (hash, allowedTabs = sessionTabFilters) => {
  if (!hash || !hash.startsWith("#")) return null;
  const tab = hash.slice(1);
  return allowedTabs.includes(tab) ? tab : null;
};

export const buildSessionTabHash = tab => `#${tab}`;

export const sortableDateFormat = date => {
  if (date) {
    let dateFormat = new Date(date);
    return dateFormat.getTime();
  } else {
    return null;
  }
};

export const capitalizeFirstLetter = name => {
  if (name && name.length > 0) {
    let tempName = name.toLowerCase();
    return tempName.charAt(0).toUpperCase() + tempName.slice(1);
  } else {
    return "";
  }
};

export const hardWrap = ({ str, maxLength = 80, lineBreak = "<br />" }) => {
  const words = str.split(" ");
  let currentLineLength = 0;
  let result = "";

  for (let i = 0; i < words.length; i++) {
    const word = words[i];
    currentLineLength += word.length + 1;

    if (currentLineLength > maxLength) {
      result += lineBreak;
      currentLineLength = word.length + 1;
    }

    result += word + " ";
  }

  return result.trim();
};

export const averageSessionLength = statsObject => {
  return statsObject?.sessionsStarted > 0 ? (statsObject?.playtimeTotal || 0) / statsObject?.sessionsStarted : 0;
};

export const sessionsWithMetadata = (sessionMap, authUser, selectedOrg, scenarios) => {
  return sessionListWithMetadata(Object.entries(sessionMap || {}), authUser, selectedOrg, scenarios);
};

export const sessionListWithMetadata = (sessionList, authUser, selectedOrg, scenarios) => {
  return sessionList.map(([id, session]) => ({
    ...session,
    scenario: scenarios?.find(scen => scen.id === session.scenarioId),
    auth: checkSharedWith(authUser?.claims, session?.sharedWith, authUser?.userId ? [authUser.userId] : []),
    // Used to hide score in tables to assessment users
    hideScore:
      session.auth !== true &&
      session.playerId === authUser?.userId &&
      session.assessmentSession &&
      session.status !== "rated"
        ? true
        : false,
    id,
  }));
};

/**
 * Returns a string containing the session transcript, e.g. all texts spoken during the session.
 * @param {object} session
 * @return {string} The session transcript.
 */
export const sessionTranscript = session => {
  const filtered = (session.events || []).filter(ev => ev?.eventType === "BOT_LINE" || ev?.eventType === "PLAYER_LINE");
  return filtered.reduce((acc, ev) => {
    const name = ev?.eventType === "BOT_LINE" ? "Bot" : "Player";
    acc += `${name}: ${ev?.text}\n`;
    return acc;
  }, "");
};

/**
 * Maps session.stats to display rows.
 * Includes only finite numeric values and resolves title/description from translations when available.
 *
 * @param {object} stats
 * @param {object} localeTranslations
 * @returns {{key: string, title: string, description: string, value: number}[]}
 */
export const sessionStatsRows = (stats, localeTranslations = {}) => {
  if (!stats || typeof stats !== "object" || Array.isArray(stats)) return [];

  return Object.entries(stats).reduce((acc, [key, value]) => {
    if (typeof value !== "number" || !Number.isFinite(value)) {
      return acc;
    }

    const translation =
      localeTranslations?.sessionStats?.[key] ||
      localeTranslations?.stats?.[key] ||
      localeTranslations?.settings?.[key] ||
      {};

    acc.push({
      key,
      title: translation.title || translation.name || key,
      description: translation.description || "",
      value,
    });
    return acc;
  }, []);
};

/**
 * Compares a feedback rating object with a baseline and returns a number to represent the feedback.
 * @param {*} baseline
 * @param {*} feedback
 * @returns {number} -1 if negative feedback, 1 if positive, else 0.
 */
export const hasFeedback = (baseline, feedback) => {
  if (!baseline || !feedback) return 0;
  const pos = baseline.ratingKey === feedback.ratingKey;
  const neg = feedback.ratingKey && baseline.ratingKey !== feedback.ratingKey;
  // Also check case where we know a specific ratingKey is considered positive, others considered negative
  // Also check the case where we compare text?
  return pos ? 1 : neg ? -1 : 0;
};

// Copied from node-functions
/**
 * Checks the ratings object to find all ratingData with a specific source type.
 * It also considers the root/default data as a model sourceType for backward compatbility.
 *
 * @param {FictiveSchema.SessionEvaluation} evalObj the complete evaluation object
 * @param {string} sourceType the type of source
 * @returns {[string, FictiveSchema.RatingData][]} an array of [sourceId, ratingData] pairs sorted by latest first
 */
export const getRatingBy = (evalObj, sourceType) => {
  const matchingEntries = Object.entries(evalObj?.ratings || {})
    .filter(([, ratingObj]) => ratingObj.sourceType === sourceType)
    // We can sort ISO dates lexicographically
    .sort(([, aRatingObj], [, bRatingObj]) => (aRatingObj.created < bRatingObj.created ? 1 : -1));
  if (sourceType === "model" && (evalObj?.ratingKey || !isNil(evalObj?.ratingScore))) {
    // Backward compatibility let's us consider the data at the root of the evalObj and not under .ratings as a model sourceType
    matchingEntries.push([
      "default",
      {
        created: new Date().toISOString(), // Actually, the rating would be much older than this but we have no data on it
        ratingKey: evalObj.ratingKey,
        ratingScore: evalObj.ratingScore,
        metric: evalObj.dataPoint,
        text: evalObj.dataText,
        sourceType: "model",
      },
    ]);
  } else if (sourceType === "user" && evalObj?.feedback?.suggestedKey) {
    // Backward compatibility let's us consider the data at the root of the evalObj and not under .ratings as a model sourceType
    matchingEntries.push([
      "feedback",
      {
        created: evalObj.feedback.created,
        ratingKey: evalObj.feedback.suggestedKey,
        ratingScore: evalObj.feedback.suggestedScore,
        sourceType: "user",
        comment: evalObj.feedback.comment,
      },
    ]);
  }
  return matchingEntries;
};

/**
 * Get only the most recent ratingData with a specific source type.
 *
 * @param {FictiveSchema.SessionEvaluation} evalObj
 * @param {string} sourceType
 * @returns {[string, FictiveSchema.RatingData]} [sourceId, ratingData] pair or ["", null]
 */
export const mostRecentRatingBy = (evalObj, sourceType) => {
  return getRatingBy(evalObj, sourceType)?.[0] || ["", null];
};

const shareableTeams = { all: "All users", admins: "Admins" };

export const sessionSharingTargets = (state, authUser, activeSession) => {
  // Defensive programming: handle null/undefined inputs
  state = state || {};
  authUser = authUser || {};
  activeSession = activeSession || {};

  const users = state.users || [];
  const organization = state.organization || {};
  const selectedOrgId = organization?.id || authUser?.organization;
  const sessionOrgId = activeSession?.organizationId;
  const canEnumerateUsersInSelectedOrg = !!selectedOrgId && checkFullUser(authUser?.claims, selectedOrgId);
  let organizations = state.organizations || [organization];
  const orgLookup = organizations.reduce((acc, org) => {
    if (org && org.id) {
      acc[org.id] = org.displayName || org.id;
    }
    return acc;
  }, {});

  const lookupUser = (id, usr) => {
    usr = usr || {};
    usr.type = "user";
    usr.id = id;
    const lookedUpUser = users.find(u => u && u.id === id);
    const sessionUser =
      id === activeSession?.playerId
        ? { displayName: activeSession?.playerDisplayName, organization: activeSession.organizationId }
        : null;
    const authedUser = id === authUser?.userId ? authUser : null;

    usr.displayName =
      usr.displayName || lookedUpUser?.displayName || sessionUser?.displayName || authedUser?.displayName || ANON_NAME;

    if (id === authUser?.userId) {
      usr.subtitle = "( You )";
    } else {
      const org =
        usr.organization || lookedUpUser?.organization || sessionUser?.organization || authedUser?.organization;
      usr.subtitle = orgLookup[org] || org || id || "Unknown";
    }
    return usr;
  };

  let possibleShares = [];

  // Add the session player as a fixed user first
  if (activeSession?.playerId) {
    possibleShares.push(lookupUser(activeSession.playerId, { readOnly: true }));
  }

  const teamOrgIds = Array.from(new Set([sessionOrgId, selectedOrgId].filter(Boolean)));
  for (const orgId of teamOrgIds) {
    for (const teamId in shareableTeams) {
      const shareName = shareableTeams[teamId];
      possibleShares.push({
        id: `${orgId}_${teamId}`,
        type: "team",
        displayName: shareName,
        subtitle: orgLookup[orgId] ?? orgId,
      });
    }
  }

  // Find all named users in selected org, but only when user has selectedOrg_all claim.
  const namedUsers = canEnumerateUsersInSelectedOrg
    ? users.filter(
        usr => usr && usr.organization === selectedOrgId && usr.displayName && usr.displayName.trim().length > 0
      )
    : [];
  if (namedUsers.length) {
    possibleShares = [
      ...possibleShares,
      ...namedUsers.filter(usr => usr.id !== activeSession?.playerId).map(usr => lookupUser(usr.id, usr)),
    ];
  }

  // Finally, for completeness, add all ids in current sharedWith that we haven't seen yet
  const currentSharedWith = Object.keys(activeSession?.sharedWith || {});
  for (const id of currentSharedWith) {
    if (!possibleShares.find(s => s.id === id)) {
      possibleShares.push(lookupUser(id));
    }
  }
  return possibleShares;
};

/**
 * Checks if a user can edit the session player display name.
 * Allowed when user is manager/admin in the session org, or the session player themselves.
 *
 * @param {object} activeSession
 * @param {object} authUser
 * @returns {boolean}
 */
export const canEditSessionPlayerDisplayName = (activeSession, authUser) => {
  if (!activeSession || !authUser) return false;

  const isManagerForSessionOrg = checkManager(authUser?.claims, activeSession?.organizationId);
  const isSessionPlayer = !!authUser?.userId && authUser.userId === activeSession?.playerId;

  return isManagerForSessionOrg || isSessionPlayer;
};

/**
 * Get only the most recent ratingData with a specific source type.
 *
 * @param {FictiveSchema.Session} session
 * @returns {object} updateObj suitable for passing to firebase.update()
 */
export const deleteSessionTranscripts = session => {
  const updateObj = {};
  if (!session) return updateObj;
  if (session?.events && Array.isArray(session.events)) {
    session.events.forEach((event, index) => {
      if (event?.eventType === "PLAYER_LINE") {
        if (!isNil(event.text)) updateObj[`events/${index}/text`] = null;
      }
    });
  }
  if (!isEmpty(session?.evaluation?.sessionSummary)) {
    updateObj["evaluation/sessionSummary"] = null;
  }
  return updateObj;
};
